import React, { useState, useEffect, Suspense, lazy } from 'react';
import { auth, db } from './firebase';
import { signOut } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";

// --- LAZY LOADING COMPONENTS ---
const ProfileForm = lazy(() => import('./components/ProfileForm'));
const DigitalGarage = lazy(() => import('./components/DigitalGarage'));
const EventCalendar = lazy(() => import('./components/EventCalendar'));
const TrackPracticeBooking = lazy(() => import('./components/TrackPracticeBooking'));
const PointsTable = lazy(() => import('./components/PointsTable')); 
const PracticeApproval = lazy(() => import('./components/PracticeApproval')); 
const ChampionshipManager = lazy(() => import('./components/ChampionshipManager'));
const AdminSettings = lazy(() => import('./components/AdminSettings'));
const EventManager = lazy(() => import('./components/EventManager'));
const EntryApproval = lazy(() => import('./components/EntryApproval'));
const AgreementManager = lazy(() => import('./components/AgreementManager'));
const RacingTrackManager = lazy(() => import('./components/RacingTrackManager'));
const CompetitorClassManager = lazy(() => import('./components/CompetitorClassManager'));
const UserManagement = lazy(() => import('./components/UserManagement'));
const ArchiveCenter = lazy(() => import('./components/ArchiveCenter'));
const RaceResultEntry = lazy(() => import('./components/RaceResultEntry'));
const PaymentManager = lazy(() => import('./components/PaymentManager'));
const ReportsCenter = lazy(() => import('./components/ReportsCenter'));

const TabLoader = () => (
  <div className="d-flex flex-column justify-content-center align-items-center vh-100 bg-white theme-poppins">
    <div className="spinner-border text-danger mb-2" role="status"></div>
    <small className="text-muted text-uppercase fw-bold tracking-wider" style={{ fontSize: '10px' }}>Loading System...</small>
  </div>
);

const Dashboard = ({ user, vehicles, forceProfile }) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [showSidebar, setShowSidebar] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [profileData, setProfileData] = useState({ fullName: '', profilePicUrl: '' });

  useEffect(() => {
    if (forceProfile) setActiveTab('profile');
  }, [forceProfile]);

  useEffect(() => {
    if (user?.uid) {
      const unsub = onSnapshot(doc(db, "competitors", user.uid), (doc) => {
        if (doc.exists()) setProfileData(doc.data());
      });
      return () => unsub();
    }
  }, [user]);

  const handleLogout = () => signOut(auth);
  const toggleSidebar = () => setShowSidebar(!showSidebar);
  const toggleMinimize = () => setIsMinimized(!isMinimized);

  const handleTabChange = (tab) => {
    if (forceProfile && tab !== 'profile') return;
    setActiveTab(tab);
    if (window.innerWidth < 768) setShowSidebar(false);
  };

  const displayName = profileData.fullName || user.displayName || user.email.split('@')[0];
  const roleLabel = user.role === 'super_admin' ? 'Super Admin' : user.role === 'admin' ? 'Administrator' : 'Competitor';

  return (
    <div className="container-fluid p-0 bg-white min-vh-100 theme-poppins overflow-hidden">
      {/* --- TOP NAVBAR --- */}
      <nav className="navbar navbar-dark bg-dark shadow position-fixed top-0 w-100 px-2 px-md-3 py-0 border-bottom border-danger border-4" style={{ zIndex: 1300, height: '70px' }}>
        <div className="d-flex align-items-center w-100 h-100">
          <button className="btn btn-dark d-md-none border-0 me-1" onClick={toggleSidebar}>
            <i className={`bi ${showSidebar ? 'bi-x-lg' : 'bi-list'} fs-2`}></i>
          </button>
          <div className="d-flex align-items-center">
            <div className="bg-white rounded-circle p-1 me-2 d-flex align-items-center justify-content-center shadow-sm" style={{ width: '45px', height: '45px' }}>
              <img src="/logo.jpg" alt="Logo" style={{ maxWidth: '100%', maxHeight: '100%', borderRadius: '50%' }} />
            </div>
            <div className="d-flex flex-column lh-1">
              <span className="fw-black text-white d-none d-sm-block text-uppercase" style={{ fontSize: '25px', letterSpacing: '0.5px' }}>
                Adrenaline <span className="text-danger">Motorsports SL</span>
              </span>
              <span className="fw-bold text-white d-sm-none" style={{ fontSize: '14px' }}>AMSL</span>
              <small className="text-white d-none d-md-block fw-bold" style={{ fontSize: '9px', letterSpacing: '1px' }}>SRI LANKA ASSOCIATION</small>
            </div>
          </div>
          <div className="ms-auto d-flex align-items-center">
            <div className="text-end me-3 d-none d-sm-flex flex-column justify-content-center text-white">
              <div className="text-uppercase text-truncate" style={{ fontSize: '14px', maxWidth: '500px' }}>{displayName}</div>
              <small className={`${user.role === 'super_admin' ? 'text-warning' : 'text-danger'} fw-bold text-uppercase`} style={{ fontSize: '9px' }}>{roleLabel}</small>
            </div>
            <img src={profileData.profilePicUrl || `https://ui-avatars.com/api/?name=${displayName}&background=ce1126&color=fff`}
              alt="User" className="rounded-circle border border-2 border-danger shadow-sm flex-shrink-0"
              style={{ width: '42px', height: '42px', objectFit: 'cover' }} />
            <button onClick={handleLogout} className="logout-btn ms-3 shadow-sm flex-shrink-0" title="Sign Out">
              <i className="bi bi-power fs-5"></i>
            </button>
          </div>
        </div>
      </nav>

      {showSidebar && <div className="sidebar-overlay d-md-none" onClick={toggleSidebar}></div>}

      <div className="d-flex" style={{ marginTop: '70px' }}>
        <nav className={`sidebar-container border-end bg-white ${isMinimized ? 'minimized' : ''} ${showSidebar ? 'show' : ''}`}>
          <div className="d-flex flex-column p-3 h-100">
            <div className="d-flex align-items-center justify-content-between mb-3">
              {!isMinimized && <span className="text-muted text-uppercase fw-bold px-2" style={{ letterSpacing: '1.5px', fontSize: '12px' }}>Command Center</span>}
              <button className="btn btn-sm btn-light border-0 d-none d-md-block" onClick={toggleMinimize}>
                <i className={`bi ${isMinimized ? 'bi-list' : 'bi-chevron-bar-left'} text-dark`}></i>
              </button>
            </div>

            <div className="flex-grow-1 overflow-auto custom-sidebar-scroll px-1">
              <ul className="nav nav-pills flex-column gap-1">
                <SidebarLink icon="person-badge" label="My Profile" active={activeTab === 'profile'} onClick={() => handleTabChange('profile')} minimized={isMinimized} />
                {!forceProfile && (
                  <>
                    <SidebarLink icon="car-front" label="Digital Garage" active={activeTab === 'garage'} onClick={() => handleTabChange('garage')} minimized={isMinimized} />
                    <SidebarLink icon="calendar-event" label="Event Calendar" active={activeTab === 'calendar'} onClick={() => handleTabChange('calendar')} minimized={isMinimized} />
                    <SidebarLink icon="calendar-check" label="Apply Practice" active={activeTab === 'practice_booking'} onClick={() => handleTabChange('practice_booking')} minimized={isMinimized} />
                    <SidebarLink icon="trophy" label="Championship Standings" active={activeTab === 'standings'} onClick={() => handleTabChange('standings')} minimized={isMinimized} />
                  </>
                )}

                {(user.role === 'admin' || user.role === 'super_admin') && (
                  <>
                    <div className="sidebar-divider">Race Management Center</div>
                    <SidebarLink icon="calendar-plus" label="Race Events" active={activeTab === 'event_management'} onClick={() => handleTabChange('event_management')} minimized={isMinimized} />
                    <SidebarLink icon="clipboard-check" label="Entry Approval" active={activeTab === 'entry_approval'} onClick={() => handleTabChange('entry_approval')} minimized={isMinimized} />
                    <SidebarLink icon="pencil-square" label="Race Results" active={activeTab === 'result_entry'} onClick={() => handleTabChange('result_entry')} minimized={isMinimized} />
                    <SidebarLink icon="check2-square" label="Practice Approvals" active={activeTab === 'practice_approval'} onClick={() => handleTabChange('practice_approval')} minimized={isMinimized} />
                    <SidebarLink icon="file-earmark-bar-graph" label="Reports Center" active={activeTab === 'reports'} onClick={() => handleTabChange('reports')} minimized={isMinimized} />
                  </>
                )}

                {(user.role === 'admin' || user.role === 'super_admin') && (
                  <>
                    <div className="sidebar-divider">Race Setup Center</div>
                    <SidebarLink icon="geo-alt" label="Track Management" active={activeTab === 'track_management'} onClick={() => handleTabChange('track_management')} minimized={isMinimized} />
                    <SidebarLink icon="award" label="Championship Registry" active={activeTab === 'champ_manager'} onClick={() => handleTabChange('champ_manager')} minimized={isMinimized} />
                    <SidebarLink icon="gear" label="Groups & Classes" active={activeTab === 'admin_settings'} onClick={() => handleTabChange('admin_settings')} minimized={isMinimized} />
                    <SidebarLink icon="grid" label="Competitor Classes" active={activeTab === 'competitor_class'} onClick={() => handleTabChange('competitor_class')} minimized={isMinimized} />
                    <SidebarLink icon="cash-stack" label="Payments Manager" active={activeTab === 'payment_manager'} onClick={() => handleTabChange('payment_manager')} minimized={isMinimized} />
                    <SidebarLink icon="file-text" label="Legal Agreements" active={activeTab === 'agreement_manager'} onClick={() => handleTabChange('agreement_manager')} minimized={isMinimized} />
                  </>
                )}

                {user.role === 'super_admin' && (
                  <>
                    <div className="sidebar-divider">Security</div>
                    <SidebarLink icon="shield-lock" label="Authority Control" active={activeTab === 'user_management'} onClick={() => handleTabChange('user_management')} minimized={isMinimized} />
                    <SidebarLink icon="archive" label="Archive Center" active={activeTab === 'archive_center'} onClick={() => handleTabChange('archive_center')} minimized={isMinimized} />
                  </>
                )}
              </ul>
            </div>
          </div>
        </nav>

        <main className={`main-content ${isMinimized ? 'content-expanded' : 'content-collapsed'}`}>
          <Suspense fallback={<TabLoader />}>
            <div className="animate-fade-in content-inner">
              {activeTab === 'profile' && <ProfileForm user={user} />}
              {!forceProfile && (
                <>
                  {activeTab === 'garage' && <DigitalGarage user={user} />}
                  {activeTab === 'calendar' && <EventCalendar vehicles={vehicles} user={user} />}
                  {activeTab === 'practice_booking' && <TrackPracticeBooking user={user} />}
                  {/* isPublicView={false} because this is within the authenticated Dashboard */}
                  {activeTab === 'standings' && <PointsTable user={user} isPublicView={false} />}
                </>
              )}
              {(user.role === 'admin' || user.role === 'super_admin') && (
                <>
                  {activeTab === 'event_management' && <EventManager />}
                  {activeTab === 'entry_approval' && <EntryApproval />}
                  {activeTab === 'result_entry' && <RaceResultEntry />}
                  {activeTab === 'practice_approval' && <PracticeApproval />}
                  {activeTab === 'champ_manager' && <ChampionshipManager />}
                  {activeTab === 'reports' && <ReportsCenter />}
                  {activeTab === 'track_management' && <RacingTrackManager />}
                  {activeTab === 'admin_settings' && <AdminSettings />}
                  {activeTab === 'competitor_class' && <CompetitorClassManager />}
                  {activeTab === 'payment_manager' && <PaymentManager />}
                  {activeTab === 'agreement_manager' && <AgreementManager />}
                </>
              )}
              {user.role === 'super_admin' && (
                <>
                  {activeTab === 'user_management' && <UserManagement />}
                  {activeTab === 'archive_center' && <ArchiveCenter />}
                </>
              )}
            </div>
          </Suspense>
        </main>
      </div>

      <style>{`
        .sidebar-container { height: calc(100vh - 70px); position: fixed; left: 0; top: 70px; width: 260px; transition: 0.3s ease; z-index: 1050; background: #ffffff; border-right: 1px solid #eee; }
        .sidebar-container.minimized { width: 80px !important; }
        .main-content { flex-grow: 1; min-height: calc(100vh - 70px); transition: margin-left 0.3s ease; background: #f8f9fa; }
        @media (min-width: 768px) {
          .content-collapsed { margin-left: 260px; width: calc(100% - 260px); }
          .content-expanded { margin-left: 80px; width: calc(100% - 80px); }
        }
        .content-inner { padding: 2rem; width: 100%; }
        .sidebar-btn { display: flex; align-items: center; padding: 0.75rem 1rem; border-radius: 10px; color: #444; border: none; background: transparent; width: 100%; transition: 0.2s; text-align: left;}
        .sidebar-btn:hover { background: #f8f9fa; color: #ce1126; }
        .sidebar-btn.active { background: #ce1126; color: white !important; }
        .sidebar-btn i { font-size: 1.2rem; min-width: 35px; }
        .sidebar-btn span { font-weight: 600; font-size: 13px; white-space: nowrap; }
        .minimized .sidebar-btn { justify-content: center; padding: 0.75rem 0; }
        .minimized .sidebar-btn i { min-width: unset; }
        .minimized span, .minimized .sidebar-divider { display: none; }
        .sidebar-divider { font-size: 10px; font-weight: 800; color: #ce1126; padding: 20px 15px 5px; text-transform: uppercase; letter-spacing: 1px; opacity: 0.7; }
        .logout-btn { background: #ce1126; color: white; border: none; width: 40px; height: 40px; border-radius: 10px; display: flex; align-items: center; justify-content: center; transition: 0.2s;}
        .sidebar-overlay { position: fixed; top: 70px; left: 0; width: 100vw; height: 100vh; background: rgba(0,0,0,0.4); z-index: 1040; }
        @media (max-width: 768px) {
          .sidebar-container { left: -100%; top: 70px; }
          .sidebar-container.show { left: 0; }
          .main-content { margin-left: 0 !important; width: 100% !important; }
        }
      `}</style>
    </div>
  );
};

const SidebarLink = ({ icon, label, active, onClick, minimized }) => (
  <button onClick={onClick} className={`sidebar-btn mb-1 ${active ? 'active' : ''}`} title={minimized ? label : ''}>
    <i className={`bi bi-${icon}`}></i>
    {!minimized && <span>{label}</span>}
  </button>
);

export default Dashboard;